<script setup lang="ts">
import { computed, ComputedRef, onBeforeUnmount, onMounted, Ref, ref, shallowRef, ShallowRef, triggerRef, watch } from 'vue'
import { Chart } from 'highcharts-vue'
import Highcharts from 'highcharts'
import 'highcharts/modules/annotations'
import 'highcharts/modules/xrange'
import 'highcharts/modules/exporting'
import 'highcharts/modules/export-data'
import 'highcharts/modules/no-data-to-display'
import 'highcharts/modules/offline-exporting'

import { useI18n } from 'vue-i18n'
import { GenericChartBuilder } from '@/composables/charts/influx-charts/chart-builders/GenericChartBuilder'
import { useLabelPositioningWithAvoidingOverlaps } from '@/composables/charts/AnnotationLabelPositioning'
const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    chartBuilder: GenericChartBuilder
    enableClickEvent?: boolean
  }>(),
  {
    enableClickEvent: true,
  }
)
defineSlots<{
  belowChartColumns(): unknown
  aboveChartColumns(): unknown
}>()

const chart: Ref<typeof Chart | undefined> = ref()
const loading: Ref<boolean> = ref(false)
const builder: ShallowRef<GenericChartBuilder> = shallowRef(props.chartBuilder)

const allHidden: ComputedRef<boolean> = computed(() => {
  return builder.value?.allHidden
})

const options: ComputedRef<Highcharts.Options> = computed(() => {
  const compiledOptions: Highcharts.Options = {
    ...builder.value.getOptions(),
    series: builder.value.series,
  }
  return compiledOptions
})

watch(
  () => [builder.value.loading, chart.value?.chart],
  () => {
    if (builder.value.loading) {
      loading.value = true
      chart.value?.chart?.showLoading(t('charts.loading_text'))
    } else {
      loading.value = false
      chart.value?.chart?.hideLoading()
    }
  }
)

function hideAll() {
  builder.value.hideAll()
}

function showAll() {
  builder.value.showAll()
}

function handleUpdate() {
  triggerRef(builder)
}

async function reload() {
  await builder.value.load()
}

async function abortLoading() {
  await builder.value.abortLoading()
}

function expand() {
  builder.value.resumeUpdates()
  expanded.value = true
}

function collapse() {
  builder.value.pauseUpdates()
  expanded.value = false
}

const expanded: Ref<boolean> = ref(!builder.value.defaultCollapsed)

watch(
  expanded,
  async () => {
    if (expanded.value) {
      builder.value.attachUpdateHandler(handleUpdate)
      builder.value.attachChartInstance(chart as Ref<typeof Chart>)
      await builder.value.load()
    } else {
      builder.value.detachUpdateHandler(handleUpdate)
    }
  },
  { immediate: true, flush: 'post' }
)

onMounted(() => {
  if (!expanded.value) builder.value.pauseUpdates()
})
onBeforeUnmount(() => {
  abortLoading().catch()
  builder.value.detachUpdateHandler(handleUpdate)
})

function onChartCallback(chart: Highcharts.Chart) {
  useLabelPositioningWithAvoidingOverlaps(chart)
  builder.value.onChartCreated(chart)
}
</script>

<template>
  <v-glass-card style="backdrop-filter: blur(6px)">
    <v-expand-transition mode="out-in">
      <v-container
        v-if="expanded"
        class="ma-0 px-0 pt-0 pb-2"
        fluid
      >
        <v-row
          align="center"
          justify="end"
          density="comfortable"
          no-gutters
          class="mb-1 pa-0"
        >
          <v-col>
            <v-row
              class="mx-2"
              align="center"
              justify="end"
              density="comfortable"
              no-gutters
            >
              <slot name="aboveChartColumns"></slot>
            </v-row>
          </v-col>
          <v-col
            v-if="loading"
            cols="auto"
          >
            <v-btn
              variant="elevated"
              rounded="pill"
              color="error"
              prepend-icon="mdi-cancel"
              @click="abortLoading"
              >{{ $t('charts.abort') }}</v-btn
            >
          </v-col>
          <v-col
            v-else
            cols="auto"
          >
            <v-btn
              variant="elevated"
              rounded="pill"
              prepend-icon="mdi-reload"
              @click="reload"
            >
              {{ $t('charts.reload') }}
            </v-btn>
          </v-col>
        </v-row>
        <Chart
          ref="chart"
          :options="options"
          :callback="onChartCallback"
        ></Chart>
        <v-row
          align="center"
          justify="space-between"
          density="comfortable"
          no-gutters
          class="mt-2 pa-0"
        >
          <v-col
            v-if="expanded"
            cols="auto"
          >
            <v-btn
              class="mx-1"
              variant="outlined"
              rounded="pill"
              size="small"
              prepend-icon="mdi-cancel"
              @click="collapse"
              >Collapse</v-btn
            >
          </v-col>
          <v-col cols="auto">
            <v-btn
              v-if="allHidden"
              class="mx-1"
              variant="outlined"
              rounded="pill"
              size="small"
              prepend-icon="mdi-eye-outline"
              @click="showAll"
              >Show All</v-btn
            >
            <v-btn
              v-else
              variant="outlined"
              rounded="pill"
              size="small"
              prepend-icon="mdi-eye-off-outline"
              @click="hideAll"
              >Hide All</v-btn
            >
          </v-col>
          <v-col>
            <v-row
              align="center"
              justify="end"
              density="comfortable"
              no-gutters
              class="mt-0 pa-0"
            >
              <slot name="belowChartColumns"></slot>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
      <v-row
        v-else
        justify="center"
      >
        <v-col cols="auto">
          <v-card-title v-if="builder.title">{{ builder.title }}</v-card-title>
          <v-card-subtitle v-if="builder.subtitle">{{ builder.subtitle }}</v-card-subtitle>
          <v-card-actions class="justify-center mt-4"
            ><v-btn
              variant="elevated"
              color="primary"
              @click="expand"
              >Click To Expand</v-btn
            >
          </v-card-actions>
        </v-col>
      </v-row>
    </v-expand-transition>
  </v-glass-card>
</template>

<style scoped>
:deep(.highcharts-annotation-shapes *) {
  pointer-events: none !important;
}
</style>
